import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [productStats, setProductStats] = useState([]);
  const [interfaceStats, setInterfaceStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashboardRes, dailyRes, productRes, interfaceRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        analyticsAPI.getDailyStats(30),
        analyticsAPI.getStatsByProduct(),
        analyticsAPI.getStatsByInterface()
      ]);

      setStats(dashboardRes.data.data);
      setDailyStats(dailyRes.data.data.reverse());
      setProductStats(productRes.data.data);
      setInterfaceStats(interfaceRes.data.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  return (
    <div>
      <div className="header">
        <h1>Dashboard</h1>
      </div>

      {/* Statistiche principali */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Chat Totali</h3>
          <div className="stat-value">{formatNumber(stats.total.total_chats)}</div>
          <div className="stat-change">
            Ultime 24h: {formatNumber(stats.last24h.chats_24h)}
          </div>
        </div>

        <div className="stat-card">
          <h3>Messaggi Totali</h3>
          <div className="stat-value">{formatNumber(stats.total.total_messages)}</div>
          <div className="stat-change">
            Ultime 24h: {formatNumber(stats.last24h.messages_24h)}
          </div>
        </div>

        <div className="stat-card">
          <h3>Costo Totale</h3>
          <div className="stat-value">{formatCurrency(stats.total.total_cost)}</div>
          <div className="stat-change">
            Ultime 24h: {formatCurrency(stats.last24h.cost_24h)}
          </div>
        </div>

        <div className="stat-card">
          <h3>Risparmio Cache</h3>
          <div className="stat-value">{formatCurrency(stats.total.total_savings)}</div>
          <div className="stat-change">
            Cache Hit Rate: {stats.cache.cache_hit_rate || 0}%
          </div>
        </div>

        <div className="stat-card">
          <h3>Sessioni Uniche</h3>
          <div className="stat-value">{formatNumber(stats.total.unique_sessions)}</div>
          <div className="stat-change">
            Media msg/chat: {parseFloat(stats.total.avg_messages_per_chat || 0).toFixed(1)}
          </div>
        </div>

        <div className="stat-card">
          <h3>Messaggi con Errori</h3>
          <div className="stat-value">{formatNumber(stats.errors.error_count)}</div>
          <div className="stat-change negative">
            Da verificare
          </div>
        </div>

        <div className="stat-card">
          <h3>Tempo Risposta Medio</h3>
          <div className="stat-value">
            {stats.responseTime && stats.responseTime.avg_response_time
              ? `${stats.responseTime.avg_response_time}s`
              : 'N/A'}
          </div>
          <div className="stat-change">
            Min: {stats.responseTime && stats.responseTime.min_response_time ? `${stats.responseTime.min_response_time}s` : 'N/A'} |
            Max: {stats.responseTime && stats.responseTime.max_response_time ? `${stats.responseTime.max_response_time}s` : 'N/A'}
          </div>
        </div>

        <div className="stat-card">
          <h3>Risposte Totali</h3>
          <div className="stat-value">
            {formatNumber(stats.responseTime?.total_responses || 0)}
          </div>
          <div className="stat-change">
            Deviazione std: {stats.responseTime && stats.responseTime.stddev_response_time
              ? `${stats.responseTime.stddev_response_time.toFixed(2)}s`
              : 'N/A'}
          </div>
        </div>
      </div>

      {/* Grafico andamento giornaliero */}
      <div className="chart-container">
        <h2>Andamento Ultimi 30 Giorni</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tickFormatter={(date) => format(new Date(date), 'dd/MM')}
            />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip 
              labelFormatter={(date) => format(new Date(date), 'dd/MM/yyyy')}
              formatter={(value, name) => {
                if (name === 'total_cost_usd') return [formatCurrency(value), 'Costo'];
                return [formatNumber(value), name === 'total_chats' ? 'Chat' : 'Messaggi'];
              }}
            />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="total_chats" stroke="#667eea" name="Chat" />
            <Line yAxisId="left" type="monotone" dataKey="total_messages" stroke="#764ba2" name="Messaggi" />
            <Line yAxisId="right" type="monotone" dataKey="total_cost_usd" stroke="#f093fb" name="Costo ($)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Statistiche per prodotto */}
        <div className="chart-container">
          <h2>Chat per Prodotto</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="product_name" />
              <YAxis />
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend />
              <Bar dataKey="total_chats" fill="#667eea" name="Chat" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Statistiche per interfaccia */}
        <div className="chart-container">
          <h2>Distribuzione per Interfaccia</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={interfaceStats}
                dataKey="total_chats"
                nameKey="interface_type"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => `${entry.interface_type}: ${formatNumber(entry.total_chats)}`}
              >
                {interfaceStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatNumber(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabella prodotti dettagliata */}
      <div className="table-container" style={{ marginTop: '20px' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Prodotto</th>
              <th>Chat</th>
              <th>Messaggi</th>
              <th>Token</th>
              <th>Costo</th>
              <th>Risparmio</th>
              <th>Media Costo/Chat</th>
            </tr>
          </thead>
          <tbody>
            {productStats.map((product, index) => (
              <tr key={index}>
                <td><strong>{product.product_name}</strong></td>
                <td>{formatNumber(product.total_chats)}</td>
                <td>{formatNumber(product.total_messages)}</td>
                <td>{formatNumber(product.total_tokens)}</td>
                <td>{formatCurrency(product.total_cost_usd)}</td>
                <td style={{ color: '#28a745' }}>{formatCurrency(product.total_savings_usd)}</td>
                <td>{formatCurrency(product.avg_cost_per_chat)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;

